import React, { useState, useEffect, useMemo, Component } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import '../pagination/pagination.css';
import { IoAddSharp } from 'react-icons/io5';
import Pagination from '../pagination/pagination';
import Select from 'react-select';
import WindowedSelect from 'react-windowed-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { RiRefreshLine } from 'react-icons/ri';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import LeaseApiService from '../../services/apiService';
import NoAccess from '../../../Routes/noAccess';

const STORAGE_KEY = 'leaseGeneralInvoiceListState.v2';

const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#F5F5F5' : null,
        color: '#000',
        ':hover': { backgroundColor: '#F5F5F5', color: '#000' },
    }),
};

const getSelectedFinancialYear = () => {
    try {
        const saved = JSON.parse(localStorage.getItem('selectedFinancialYear'));
        if (saved?.id) return saved.id;
        const years = JSON.parse(localStorage.getItem('financialYear')) || [];
        const active = years.find((fy) => fy.isActive);
        return active?.id || '';
    } catch (error) {
        return '';
    }
};

const emptyFilters = () => ({
    voucherNo: '',
    tenant: null,
    invoiceType: null,
    // Default to the current month, e.g. 01 Sep to 30 Sep
    fromDate: moment().startOf('month').toDate(),
    toDate: moment().endOf('month').toDate(),
});

// Read synchronously so "no access" is not flashed before the effect runs.
const readPermission = (formName) => {
    try {
        const stored = JSON.parse(localStorage.getItem('RoleAccess')) || [];
        return stored.find((acc) => acc.formName === formName);
    } catch (error) {
        return undefined;
    }
};

const loadPersistedState = () => {
    try {
        const raw = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
        if (!raw) return null;
        return {
            page: raw.page || 1,
            filters: {
                voucherNo: raw.voucherNo || '',
                tenant: raw.tenantNo ? { value: { value: raw.tenantNo, text: raw.tenantLabel }, label: `${raw.tenantNo} - ${raw.tenantLabel}` } : null,
                invoiceType: raw.invoiceType ? { value: { value: raw.invoiceType, text: raw.invoiceTypeLabel }, label: raw.invoiceTypeLabel } : null,
                fromDate: raw.fromDate ? moment(raw.fromDate).toDate() : emptyFilters().fromDate,
                toDate: raw.toDate ? moment(raw.toDate).toDate() : emptyFilters().toDate,
            },
        };
    } catch (error) {
        return null;
    }
};

const persistState = (page, filters) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
            page,
            voucherNo: filters?.voucherNo || '',
            tenantNo: filters?.tenant?.value?.value || '',
            tenantLabel: filters?.tenant?.value?.text || '',
            invoiceType: filters?.invoiceType?.value?.value || '',
            invoiceTypeLabel: filters?.invoiceType?.value?.text || '',
            fromDate: filters?.fromDate ? moment(filters.fromDate).format('YYYY-MM-DD') : '',
            toDate: filters?.toDate ? moment(filters.toDate).format('YYYY-MM-DD') : '',
        }));
    } catch (error) {
        // ignore storage failures
    }
};

const GeneralListing = () => {
    const apiService = new LeaseApiService();
    const navigate = useNavigate();
    const persisted = loadPersistedState();

    const [permissions] = useState(() => readPermission('Lease General Invoice'));
    const [FilterTab, setFilterTab] = useState(true);
    const [loading, setLoading] = useState(true);
    const [rowData, setRowData] = useState([]);
    const [pagination, setPagination] = useState();
    const [pageSize, setPageSize] = useState(50);
    const [contextMenu, setContextMenu] = useState(null);

    const [tenantList, setTenantList] = useState([]);
    const [invoiceTypeList, setInvoiceTypeList] = useState([]);
    const [financialYear] = useState(getSelectedFinancialYear());
    const [filters, setFilters] = useState(persisted?.filters || emptyFilters());

    useEffect(() => {
        const handleClickOutside = () => setContextMenu(null);
        window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, []);

    useEffect(() => {
        fetchDropdownData();
        getItems(persisted?.page || 1, persisted?.filters || emptyFilters());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchDropdownData = async () => {
        try {
            const res = await apiService.post('GEN_INV_DROPDOWNS', { dropdownTypes: ['InvoiceTypes', 'Tenants'] });
            setInvoiceTypeList(res?.data?.dropdowns?.InvoiceTypes || []);
            setTenantList(res?.data?.dropdowns?.Tenants || []);
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    };

    const buildParams = (newPageNo, currentFilters) => {
        const params = new URLSearchParams();
        params.append('pageNo', newPageNo || 1);
        params.append('pageSize', pageSize || 50);
        if (currentFilters?.voucherNo?.trim()) params.append('voucherNo', currentFilters.voucherNo.trim());
        if (currentFilters?.tenant?.value?.value) params.append('tenantNo', currentFilters.tenant.value.value);
        if (currentFilters?.invoiceType?.value?.value) params.append('invoiceType', currentFilters.invoiceType.value.value);
        if (currentFilters?.fromDate) params.append('fromDate', moment(currentFilters.fromDate).format('YYYY-MM-DD'));
        if (currentFilters?.toDate) params.append('toDate', moment(currentFilters.toDate).format('YYYY-MM-DD'));
        if (financialYear) params.append('financialYear', financialYear);
        return params;
    };

    const getItems = async (newPageNo = pagination?.currentPage || 1, currentFilters = filters) => {
        try {
            setLoading(true);
            const params = buildParams(newPageNo, currentFilters);
            const res = await apiService.getApiParamater('GEN_INV_LIST', `?${params.toString()}`);
            const statusCode = res?.data?.statusCode ?? res?.status;
            if (statusCode === 204) {
                setRowData([]);
                setPagination({ currentPage: 1, totalPages: 1, totalRecords: 0 });
            } else {
                setRowData(res?.data?.result || []);
                setPagination(res?.data?.paginationData);
            }
            persistState(newPageNo, currentFilters);
        } catch (error) {
            console.error('Error fetching items:', error);
            toast.error('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        getItems(1, filters);
    };

    const handleReset = () => {
        const cleared = emptyFilters();
        setFilters(cleared);
        getItems(1, cleared);
    };

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const handleNew = () => {
        navigate('/Lease/Management/Lease/GeneralInvoice/Create');
    };

    const handleView = (row) => {
        navigate(`/Lease/Management/Lease/GeneralInvoice/view/${row?.voucherNo}?financialYear=${encodeURIComponent(row?.financialYear || '')}`);
    };

    const handleEdit = (row) => {
        navigate(`/Lease/Management/Lease/GeneralInvoice/edit/${row?.voucherNo}?financialYear=${encodeURIComponent(row?.financialYear || '')}`);
    };

    const handleMenuAction = (action, row) => {
        if (action === 'view') handleView(row);
        else if (action === 'edit') handleEdit(row);
        setContextMenu(null);
    };

    const dateFormatter = (params) => (params.value ? moment(params.value).format('DD-MMM-YYYY') : '');
    const amountFormatter = (params) => (typeof params.value === 'number' ? params.value.toFixed(3) : params.value);

    const columnDefs = useMemo(() => [
        {
            headerName: '', field: 'voucherNo', cellRenderer: BtnCellRenderer, pinned: 'left', width: 40, suppressSizeToFit: true,
        },
        { headerName: 'Voucher No', field: 'voucherNo', filter: true, floatingFilter: true, suppressMovable: true, width: 140 },
        { headerName: 'Date', field: 'postingDate', filter: true, floatingFilter: true, valueFormatter: dateFormatter, suppressMovable: true, width: 130 },
        { headerName: 'Invoice Type', field: 'invoiceType', filter: true, floatingFilter: true, suppressMovable: true, width: 140 },
        { headerName: 'Tenant No', field: 'tenantNo', filter: true, floatingFilter: true, suppressMovable: true, width: 120 },
        { headerName: 'Tenant Name', field: 'tenantName', filter: true, floatingFilter: true, suppressMovable: true, width: 220 },
        { headerName: 'Ref No', field: 'refNo', filter: true, floatingFilter: true, suppressMovable: true, width: 130 },
        {
            headerName: 'Amount', field: 'amount', filter: true, floatingFilter: true, suppressMovable: true, width: 130,
            valueFormatter: amountFormatter, cellStyle: { textAlign: 'right' },
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], []);

    return (
        <div className="p-3">
            {permissions?.canView === true ? (
                <>
                    <div className="cost-center-section">
                        <div className="d-flex mt-3 cursor-pointer slide-header" onClick={() => setFilterTab(!FilterTab)}>
                            <div className="param-label pt-1">Search Parameters</div>
                        </div>
                        <div className={`slide-content ${FilterTab ? 'open' : 'closed'}`}>
                            <div className={`border-box2 p-3 ${FilterTab ? 'slide-in' : 'slide-out'}`}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '15px' }}>
                                    <div>
                                        <label className="form-label">Voucher No</label>
                                        <input
                                            type="text"
                                            className="p-2 form-control"
                                            placeholder="Voucher No"
                                            value={filters.voucherNo}
                                            onChange={(e) => handleFilterChange('voucherNo', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Tenant</label>
                                        <WindowedSelect
                                            className="p-0"
                                            theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            menuShouldScrollIntoView={false}
                                            styles={selectStyles}
                                            isClearable
                                            value={filters.tenant}
                                            onChange={(opt) => handleFilterChange('tenant', opt)}
                                            options={tenantList?.map((rec) => ({ value: rec, label: `${rec?.value} - ${rec?.text}` }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Invoice Type</label>
                                        <Select
                                            className="p-0"
                                            theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            menuShouldScrollIntoView={false}
                                            styles={selectStyles}
                                            isClearable
                                            value={filters.invoiceType}
                                            onChange={(opt) => handleFilterChange('invoiceType', opt)}
                                            options={invoiceTypeList?.map((rec) => ({ value: rec, label: rec?.text }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">From Date</label>
                                        <DatePicker
                                            selected={filters.fromDate}
                                            onChange={(date) => handleFilterChange('fromDate', date)}
                                            dateFormat="dd-MMM-yyyy"
                                            className="p-2 form-control w-100"
                                            isClearable
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">To Date</label>
                                        <DatePicker
                                            selected={filters.toDate}
                                            onChange={(date) => handleFilterChange('toDate', date)}
                                            dateFormat="dd-MMM-yyyy"
                                            className="p-2 form-control w-100"
                                            isClearable
                                        />
                                    </div>
                                    <div className="d-flex gap-2 align-items-end">
                                        <div onClick={handleSearch} className="mainbtnGry flex-end gap-2 w-50">
                                            <FaFileCircleExclamation color="#fff" className="center mt-1 mx-2" />
                                            <div className="center">Search</div>
                                        </div>
                                        <div onClick={handleReset} className="mainbtnRed flex-end gap-2 w-50">
                                            <RiRefreshLine color="#fff" className="center mt-1 mx-2" />
                                            <div className="center">Reset</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="border-box2 p-3 mt-2">
                        {loading ? (
                            <div>Loading...</div>
                        ) : (
                            <>
                                <div className="d-flex justify-contant-between">
                                    <h5 className="col mainInvHead">Lease General Invoice List</h5>
                                    <div className="d-flex justify-content-end gap-1">
                                        {permissions?.canAdd === true && (
                                            <div className="col center">
                                                <button className="mainbtn" type="button" onClick={handleNew}>
                                                    <IoAddSharp color="#fff" className="center mt-1" /> <div>New Invoice</div>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="gridshad mt-2">
                                    <div className="ag-theme-quartz" style={{ height: 510 }}>
                                        <AgGridReact
                                            getRowStyle={(params) => (params.node.rowIndex % 2 === 0 ? { background: '#F0EDF5' } : { background: '#fff' })}
                                            rowData={rowData}
                                            columnDefs={columnDefs}
                                            enableFilter={true}
                                            floatingFilter={true}
                                            frameworkComponents={{ BtnCellRenderer }}
                                            context={{ setContextMenu }}
                                            onGridReady={(params) => params.api.sizeColumnsToFit()}
                                            onGridSizeChanged={(params) => params.api.sizeColumnsToFit()}
                                        />
                                    </div>
                                    <Pagination pageSize={pageSize} setPageSize={setPageSize} pagination={pagination} getItems={(page) => getItems(page, filters)} />
                                </div>
                            </>
                        )}
                    </div>
                    {contextMenu && (
                        <div
                            style={{
                                position: 'absolute',
                                top: contextMenu.mouseY,
                                left: contextMenu.mouseX,
                                backgroundColor: 'white',
                                border: '1px solid gray',
                                zIndex: 1000,
                                padding: '10px 30px',
                                borderRadius: '8px',
                                boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
                            }}
                        >
                            <div style={{ display: 'grid' }}>
                                <button className="btn btn-secondary" onClick={() => handleMenuAction('view', contextMenu.data)}>View</button>
                            </div>
                            <div style={{ display: 'grid' }}>
                                <button className="btn btn-secondary mt-2" onClick={() => handleMenuAction('edit', contextMenu.data)}>Edit</button>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <NoAccess />
            )}
        </div>
    );
};

class BtnCellRenderer extends Component {
    constructor(props) {
        super(props);
        this.handleClick = this.handleClick.bind(this);
    }

    handleClick(e) {
        e.stopPropagation();
        const { context, data } = this.props;
        if (context && context.setContextMenu) {
            context.setContextMenu({
                mouseX: e.clientX,
                mouseY: e.clientY,
                data,
            });
        }
    }

    render() {
        return (
            <div className="center pt-1">
                <span onClick={this.handleClick} style={{ cursor: 'pointer', fontSize: '30px' }}>
                    &#8942;
                </span>
            </div>
        );
    }
}

export default GeneralListing;
